import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { User, MapPin, Bookmark, Send, Loader2, CheckCircle, Star, Trash2, Eye } from 'lucide-react';

const SavedCandidates = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [reachOutOpen, setReachOutOpen] = useState(false);
  const [reachOutMessage, setReachOutMessage] = useState('');
  const [sendingReachOut, setSendingReachOut] = useState(false);

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const res = await employerAPI.getSavedCandidates();
      setCandidates(res.data);
    } catch (error) {
      console.error('Error fetching saved candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnsave = async (userId) => {
    try {
      await employerAPI.saveCandidate(userId);
      setCandidates(prev => prev.filter(c => c.user_id !== userId));
      toast.success('Candidate removed');
    } catch (error) {
      toast.error('Failed to remove candidate');
    }
  };

  const handleSendReachOut = async () => {
    if (!reachOutMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingReachOut(true);
    try {
      await employerAPI.sendReachOut(selectedCandidate, reachOutMessage);
      toast.success('Reach-out sent!');
      setReachOutOpen(false);
      setReachOutMessage('');
      setSelectedCandidate(null);
    } catch (error) {
      if (error.response?.data?.detail?.includes('limit')) {
        toast.error('Monthly reach-out limit reached. Upgrade for more.');
      } else {
        toast.error('Failed to send reach-out');
      }
    } finally {
      setSendingReachOut(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="employer-saved-candidates">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-3xl font-bold text-[#0A0F1E]">Saved Candidates</h1>
          <p className="mt-2 text-[#5B6472]">Candidates you've bookmarked for later.</p>
        </div>

        {/* Candidates List */}
        {candidates.length === 0 ? (
          <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
            <Bookmark className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No saved candidates</h3>
            <p className="text-[#5B6472]">
              When you save candidates from search, they'll appear here.
            </p>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {candidates.map((candidate, i) => (
              <Card 
                key={candidate.user_id} 
                className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5 animate-fade-in cursor-pointer hover:border-[#1B5AFF]/50 transition-colors"
                style={{ animationDelay: `${i * 0.05}s` }}
                onClick={() => navigate(`/employer/talent/${candidate.user_id}`)}
                data-testid={`saved-candidate-${candidate.user_id}`}
              >
                <div className="flex items-start gap-4">
                  {candidate.photo_url ? (
                    <img src={candidate.photo_url} alt="" className="w-14 h-14 rounded-full object-cover border border-[#E8EDF5]" />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-[#F5F8FF] border border-[#E8EDF5] flex items-center justify-center">
                      <User className="w-6 h-6 text-[#5B6472]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-[#0A0F1E] truncate">
                      {candidate.first_name || candidate.last_name 
                        ? `${candidate.first_name} ${candidate.last_name}`.trim()
                        : 'Anonymous'}
                    </h3>
                    <p className="text-[#5B6472] text-sm truncate">{candidate.job_title || 'No title'}</p>
                    {candidate.location && (
                      <p className="text-[#5B6472] text-xs flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {candidate.location}
                      </p>
                    )}
                  </div>
                </div>

                {candidate.skills?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {candidate.skills.slice(0, 3).map((skill, i) => (
                      <span key={i} className="px-2 py-0.5 rounded-full bg-[#F5F8FF] text-[#0A0F1E] text-xs border border-[#E8EDF5]">
                        {skill}
                      </span>
                    ))}
                  </div>
                )}

                <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E8EDF5]">
                  <span className="text-[#5B6472] text-xs flex items-center gap-1">
                    <CheckCircle className="w-3 h-3 text-green-400" />
                    {candidate.verified_records_count || 0} verified
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); handleUnsave(candidate.user_id); }}
                      className="h-8 w-8 p-0 text-[#5B6472] hover:text-red-400 hover:bg-red-500/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      onClick={(e) => { e.stopPropagation(); setSelectedCandidate(candidate.user_id); setReachOutOpen(true); }}
                      className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-lg h-8 px-3"
                    >
                      <Send className="w-3 h-3 mr-1" />
                      Reach Out
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Reach Out Dialog */}
        <Dialog open={reachOutOpen} onOpenChange={setReachOutOpen}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
            <DialogHeader>
              <DialogTitle>Send a Reach-Out</DialogTitle>
              <DialogDescription className="text-[#5B6472]">
                Introduce yourself and explain why you're reaching out.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              value={reachOutMessage}
              onChange={(e) => setReachOutMessage(e.target.value.slice(0, 500))}
              placeholder="Hi, I'm reaching out because..."
              className="bg-[#F5F8FF]/50 border-[#E8EDF5] text-[#0A0F1E] rounded-xl min-h-[150px]"
            />
            <div className="flex justify-end text-[#5B6472] text-xs">
              {reachOutMessage.length}/500
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReachOutOpen(false)} className="border-[#E8EDF5] text-[#0A0F1E]">
                Cancel
              </Button>
              <Button 
                onClick={handleSendReachOut} 
                disabled={sendingReachOut}
                className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF]"
              >
                {sendingReachOut ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send Message
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default SavedCandidates;
